'use client'

import { useRouter } from 'next/navigation'
import { useCallback, useEffect, useRef } from 'react'
import { toast } from 'sonner'
import { authClient } from '~/lib/auth.client'

interface TelegramLoginProps {
  align?: 'left' | 'center' | 'right'
  onSuccess?: () => void
  onError?: (error: Error) => void
}

export function TelegramLogin({
  align = 'center',
  onSuccess,
  onError,
}: TelegramLoginProps) {
  const router = useRouter()
  const containerId = 'telegram-login-widget'
  const initializedRef = useRef(false)

  const handleAuth = useCallback(async (authData: {
    id: number
    first_name: string
    last_name?: string
    username?: string
    photo_url?: string
    auth_date: number
    hash: string
  }) => {
    try {
      const response = await authClient.signInWithTelegram(authData)

      if (response.error) {
        toast.error('Ошибка входа', {
          description: response.error.message ?? 'Не удалось войти через Telegram',
        })
        onError?.(new Error(response.error.message ?? 'Authentication failed'))
        return
      }

      toast.success('Вход выполнен успешно!', {
        description: 'Страница обновляется...',
      })

      onSuccess?.()

      await new Promise(resolve => setTimeout(resolve, 300))
      await authClient.getSession()
      router.refresh()

      setTimeout(() => {
        window.location.href = window.location.pathname + window.location.search
      }, 500)
    }
    catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error'
      toast.error('Ошибка входа', {
        description: message,
      })
      onError?.(error instanceof Error ? error : new Error(message))
    }
  }, [onSuccess, onError, router])

  useEffect(() => {
    if (initializedRef.current) {
      return
    }

    initializedRef.current = true

    authClient.initTelegramWidget(
      containerId,
      {
        size: 'large',
        showUserPhoto: true,
        requestAccess: true,
        lang: 'ru',
      },
      handleAuth,
    )
  }, [handleAuth])

  const justifyContent = align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center'

  return (
    <div
      id={containerId}
      className="inline-flex"
      style={{ justifyContent }}
    />
  )
}
